import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  getNumberFromValue,
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  PatientAuthorizationAllowanceBasis,
} from 'dtos/patientAuthorizationAllowanceBasis';
import type {
  PatientAuthorizationResponseDTO,
  PatientAuthorizationSaveRequestDTO,
} from 'dtos';
import {
  formatDateOnly,
} from 'utils/date';

export type AuthorizationAllowanceFormValue = {
  patientAuthorizationAllowanceId?: number;
  allowanceBasis?: 'Visits' | 'Units';
  approvedQuantity?: number | string;
  externalUsedQuantity?: number | string | null;
  codes: Array<{
    code: string;
    description?: string | null;
  }>;
};

export type AuthorizationFormValues = {
  patientAuthorizationId?: number | null;
  insuranceId?: number;
  authorizationNumber: string;
  effectiveDate: Dayjs | null;
  expirationDate: Dayjs | null;
  allowances: AuthorizationAllowanceFormValue[];
  notes: string;
};

export function getAuthorizationFormDefaults(
  authorization?: PatientAuthorizationResponseDTO | null,
  overrides?: Partial<Pick<AuthorizationFormValues, 'authorizationNumber' | 'insuranceId'>>,
): AuthorizationFormValues {
  const allowances = authorization?.allowances.length
    ? authorization.allowances.map((allowance) => ({
      patientAuthorizationAllowanceId: allowance.patientAuthorizationAllowanceId,
      allowanceBasis: allowance.allowanceBasis,
      approvedQuantity: allowance.approvedQuantity,
      externalUsedQuantity: allowance.externalUsedQuantity ?? 0,
      codes: allowance.codes.map(({ code, description }) => ({ code, description })),
    }))
    : [{
      allowanceBasis: undefined,
      approvedQuantity: undefined,
      externalUsedQuantity: 0,
      codes: [],
    }];

  const defaults: AuthorizationFormValues = {
    patientAuthorizationId: authorization?.patientAuthorizationId ?? null,
    insuranceId: authorization?.insuranceId,
    authorizationNumber: authorization?.authorizationNumber ?? '',
    effectiveDate: authorization ? dayjs(authorization.effectiveDate) : null,
    expirationDate: authorization ? dayjs(authorization.expirationDate) : null,
    allowances,
    notes: authorization?.notes ?? '',
  };

  return isNil(authorization) && !isNil(overrides)
    ? {
      ...defaults,
      ...overrides,
    }
    : defaults;
}

export function serializeAuthorization(
  values: AuthorizationFormValues,
): PatientAuthorizationSaveRequestDTO {
  const authorizationNumber = values.authorizationNumber.trim();
  return {
    patientAuthorizationId: values.patientAuthorizationId ?? null,
    insuranceId: values.insuranceId,
    authorizationNumber: notEmptyString(authorizationNumber) ? authorizationNumber : null,
    effectiveDate: formatDateOnly(values.effectiveDate ?? ''),
    expirationDate: values.expirationDate ? formatDateOnly(values.expirationDate) : undefined,
    notes: notEmptyString(values.notes) ? values.notes : null,
    allowances: values.allowances.map((allowance) => {
      const patientAuthorizationAllowanceId = getNumberFromValue(
        allowance.patientAuthorizationAllowanceId,
      );
      const { externalUsedQuantity } = allowance;
      return {
        patientAuthorizationAllowanceId: patientAuthorizationAllowanceId > 0
          ? patientAuthorizationAllowanceId
          : undefined,
        allowanceBasis: allowance.allowanceBasis,
        approvedQuantity: getNumberFromValue(allowance.approvedQuantity),
        externalUsedQuantity: externalUsedQuantity === '' || externalUsedQuantity == null
          ? 0
          : getNumberFromValue(externalUsedQuantity),
        codes: allowance.allowanceBasis === 'Units'
          ? allowance.codes.map(({ code }) => code)
          : [],
      };
    }),
  };
}

export function isPositiveWholeNumber(value: number | string | undefined): boolean {
  const quantity = getNumberFromValue(value);
  return Number.isInteger(quantity) && quantity > 0;
}

type AllowanceValidationRule = {
  validate: (allowance: AuthorizationAllowanceFormValue) => boolean;
  message: string;
};

const allowanceValidationRules: Record<
  PatientAuthorizationAllowanceBasis,
  AllowanceValidationRule[]
> = {
  [PatientAuthorizationAllowanceBasis.Visits]: [{
    validate: ({ approvedQuantity }) => isPositiveWholeNumber(approvedQuantity),
    message: 'Enter at least 1 approved visit',
  }],
  [PatientAuthorizationAllowanceBasis.Units]: [
    {
      validate: ({ codes }) => codes.length === 1 && notEmptyString(codes[0]?.code),
      message: 'Select at least one service code',
    },
    {
      validate: ({ approvedQuantity }) => isPositiveWholeNumber(approvedQuantity),
      message: 'Enter at least 1 approved unit for each service code',
    },
  ],
};

export function validateAuthorizationAllowances(
  allowances?: AuthorizationAllowanceFormValue[],
): true | string {
  if (!allowances?.length) {
    return 'At least one allowance is required';
  }

  const basis = allowances[0]?.allowanceBasis;
  if (!basis) {
    return 'Select basis';
  }

  if (allowances.some((allowance) => allowance.allowanceBasis !== basis)) {
    return 'All allowances must use the same basis';
  }

  const invalidRule = allowanceValidationRules[basis].find(({ validate }) => (
    allowances.some((allowance) => !validate(allowance))
  ));
  return invalidRule?.message ?? true;
}
